import { useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  checkAssessmentExists,
  archiveAssessment,
  createTPRAAssessment,
} from "@/modules/agent/requests/tpra-assessment";
import { getAgentStateActions } from "@/modules/agent/states";
import { AGENT_TYPES, AgentData, MultiVendorAssessmentTypes } from "@/modules/agent/types";
import {
  AgentSessionStep,
  AgentSessionStepType,
  AgentSessionStatus,
  MultiVendorAgentSession,
} from "@/modules/sessions/types";
import { userStateSelector } from "@/shared/states/user";
import { AiResponseType } from "@/shared/types/user";
import type { TPRAFormData, ExistingAssessment } from "../types";

export type FlowStep = "form" | "exists" | "archive";

interface FlowState {
  step: FlowStep;
  isLoading: boolean;
  existingAssessment: ExistingAssessment | null;
  memberHasAccess: boolean;
}

const initialState: FlowState = {
  step: "form",
  isLoading: false,
  existingAssessment: null,
  memberHasAccess: true,
};

export function useTPRAFlow(
  onClose: () => void,
  initialVendorData?: {
    vendorName: string;
    vendorDomain?: string;
    vendorLogoUrl?: string;
  }
) {
  const navigate = useNavigate();
  const [state, setState] = useState<FlowState>(initialState);
  const formDataRef = useRef<TPRAFormData | null>(
    initialVendorData
      ? {
          vendorName: initialVendorData.vendorName,
          vendorDomain: initialVendorData.vendorDomain,
          vendorLogoUrl: initialVendorData.vendorLogoUrl,
          productName: "",
          selectedControlSets: [],
        }
      : null
  );

  const reset = useCallback(() => {
    setState(initialState);
    formDataRef.current = initialVendorData
      ? {
          vendorName: initialVendorData.vendorName,
          vendorDomain: initialVendorData.vendorDomain,
          vendorLogoUrl: initialVendorData.vendorLogoUrl,
          productName: "",
          selectedControlSets: [],
        }
      : null;
  }, [initialVendorData]);

  const setupAgentDataAndNavigate = useCallback(
    (response: any, formData: TPRAFormData) => {
      const selectedResponse =
        userStateSelector.getState().aiResponseType === AiResponseType.NONE
          ? AiResponseType.LITE
          : userStateSelector.getState().aiResponseType;

      const assessment = response.assessment;
      const steps = response.steps || [];

      // Set up step data with status like old code
      const stepData = steps.map((step: AgentSessionStep) => {
        if (step.type === AgentSessionStepType.MULTI_VENDOR_PROFILE) {
          return { ...step, status: AgentSessionStatus.IN_PROGRESS };
        }
        return step;
      });

      const agentData: AgentData<
        AGENT_TYPES.MULTI_FILE_VENDOR_ASSESSMENT,
        MultiVendorAssessmentTypes.VENDOR_ASSESSMENT
      > = {
        agentType: AGENT_TYPES.MULTI_FILE_VENDOR_ASSESSMENT,
        sessionData: assessment as MultiVendorAgentSession,
        stepData,
        subType: MultiVendorAssessmentTypes.VENDOR_ASSESSMENT,
        responseQuality: selectedResponse,
        mainData: {
          sourceFilesUrls: new Map(),
          approvedIds: [],
          editedIds: [],
          vendorName: formData.vendorName,
          scopeOfIntegration: "",
        },
      };

      const { setAgentData } = getAgentStateActions();
      setAgentData(assessment.id, agentData);

      navigate(`/agent/${assessment.id}/`);
    },
    [navigate]
  );

  const handleSubmit = useCallback(
    async (formData: TPRAFormData) => {
      if (state.isLoading) return;

      formDataRef.current = formData;
      setState((s) => ({ ...s, isLoading: true }));

      // Step 1: Check if assessment exists
      let existing: ExistingAssessment | null = null;
      let memberHasAccess = true;
      try {
        const result = await checkAssessmentExists(
          formData.vendorName,
          formData.productName || undefined
        );
        if (result && result.assessments && result.assessments.length > 0) {
          existing = result.assessments[0];
          memberHasAccess = result.member_has_access ?? true;
        }
      } catch {
        // 404 or error = no existing assessment
      }

      if (existing) {
        setState({
          step: "exists",
          isLoading: false,
          existingAssessment: existing,
          memberHasAccess,
        });
        return;
      }

      // Step 2: Create new assessment
      const response = await createTPRAAssessment({
        type: "multi_file_vendor_assessment",
        multi_file_vendor_assessment: {
          doc_type: "json",
          response_quality: "lite",
          source_urls: [],
          vendor_name: formData.vendorName,
          product_name: formData.productName || undefined,
          selected_control_sets: formData.selectedControlSets,
          vendor_domain_url: formData.vendorDomain,
          vendor_logo_url: formData.vendorLogoUrl,
          vendor_filled_intake_form: false,
          contact_email: formData.contactEmail,
        },
      });

      setupAgentDataAndNavigate(response, formData);
    },
    [state.isLoading, setupAgentDataAndNavigate]
  );

  const handleViewAssessment = useCallback(() => {
    if (state.existingAssessment) {
      navigate(`/agent/${state.existingAssessment.id}/`);
    }
  }, [state.existingAssessment, navigate]);

  const handleGoBack = useCallback(() => {
    if (state.step === "archive") {
      // Go back from archive to exists
      setState((s) => ({ ...s, step: "exists" }));
    } else {
      // Go back from exists to form (reset)
      setState(initialState);
    }
  }, [state.step]);

  const handleStartNew = useCallback(() => {
    if (state.existingAssessment) {
      setState((s) => ({ ...s, step: "archive" }));
    }
  }, [state.existingAssessment]);

  const handleArchiveConfirm = useCallback(async () => {
    if (!state.existingAssessment || !formDataRef.current) return;

    setState((s) => ({ ...s, isLoading: true }));

    try {
      // Archive existing - if already archived, we can proceed
      try {
        await archiveAssessment(state.existingAssessment.id);
      } catch (archiveError: any) {
        // If assessment is already archived, we can proceed with creating new one
        const errorMessage = archiveError?.response?.data?.error_message || archiveError?.message || "";
        if (!errorMessage.toLowerCase().includes("already archived")) {
          throw archiveError;
        }
        // Assessment already archived - continue to create new
      }

      // Create new
      const formData = formDataRef.current;
      const response = await createTPRAAssessment({
        type: "multi_file_vendor_assessment",
        multi_file_vendor_assessment: {
          doc_type: "json",
          response_quality: "lite",
          source_urls: [],
          vendor_name: formData.vendorName,
          product_name: formData.productName || undefined,
          selected_control_sets: formData.selectedControlSets,
          vendor_domain_url: formData.vendorDomain,
          vendor_logo_url: formData.vendorLogoUrl,
          vendor_filled_intake_form: false,
          contact_email: formData.contactEmail,
        },
      });

      setupAgentDataAndNavigate(response, formData);
    } catch (error) {
      // Reset loading state on error so modal doesn't get stuck
      setState((s) => ({ ...s, isLoading: false }));
      console.error("Failed to create assessment:", error);
    }
  }, [state.existingAssessment, setupAgentDataAndNavigate]);

  return {
    step: state.step,
    isLoading: state.isLoading,
    existingAssessment: state.existingAssessment,
    memberHasAccess: state.memberHasAccess,
    savedFormData: formDataRef.current,
    handleSubmit,
    handleViewAssessment,
    handleGoBack,
    handleStartNew,
    handleArchiveConfirm,
    reset,
  };
}
